import { Injectable } from '@angular/core';
import { CognitoCallbackGQL, NoteMagicLinkRequestGQL } from '@cpd-app/generated';
import {
  confirmSignIn,
  ConfirmSignInOutput,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signInWithRedirect,
  signOut,
  signUp
} from 'aws-amplify/auth';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { CognitoAuthMapper } from '../../mappers/cognito-auth.mapper';

import { CognitoCallback, CognitoUser, MagicLinkResult } from '../../models';
import { UtilityService } from '../utility';

/** INFRA.7b: how long sign-in waits for the magic link request note. */
const NOTE_MAGIC_LINK_REQUEST_TIMEOUT_MS = 3000;

@Injectable({
  providedIn: 'root'
})
export class CognitoAuthService {
  constructor(
    private readonly cognitoCallbackGQL: CognitoCallbackGQL,
    // INFRA.7b. Last and optional so every existing spec that constructs this
    // service with one argument keeps working.
    private readonly noteMagicLinkRequestGQL?: NoteMagicLinkRequestGQL
  ) {}

  public signInWithGoogle() {
    signInWithRedirect({ provider: 'Google' });
  }

  public signInWithLinkedIn() {
    signInWithRedirect({ provider: { custom: 'LinkedIn' } });
  }

  public signOut(): Promise<void> {
    return signOut({
      global: false,
      oauth: {
        redirectUrl: location.origin
      }
    });
  }

  public getCurrentUser(): Observable<CognitoUser | null> {
    return from(getCurrentUser()).pipe(
      map((user) => {
        return user as CognitoUser;
      }),
      catchError(() => {
        return [null];
      })
    );
  }

  public getIdToken(): Observable<string> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        return session.tokens?.idToken?.toString();
      }),
      catchError((error) => {
        throw CognitoAuthMapper.mapToCognitoAuthError(error);
      })
    );
  }

  public signUpWithMagicLink(email: string): Observable<MagicLinkResult> {
    return from(
      signUp({
        username: email,
        password: UtilityService.generatePassword()
      })
    ).pipe(
      switchMap(() => {
        return this.signInWithMagicLink(email);
      }),
      catchError((error) => {
        if (
          error.name === 'UserLambdaValidationException' &&
          error.message.includes('email already exists')
        ) {
          return this.signInWithMagicLink(email);
        }

        throw CognitoAuthMapper.mapToCognitoAuthError(error);
      })
    );
  }

  /**
   * `noteRequest: false` is for the landing path (the link was clicked and this
   * restarts the challenge in a new tab): it sends no email, and noting there
   * would record the landing address over the one the person asked from.
   */
  public signInWithMagicLink(
    email: string,
    { noteRequest = true }: { noteRequest?: boolean } = {}
  ): Observable<MagicLinkResult> {
    return (noteRequest ? this.noteMagicLinkRequest(email) : of(null)).pipe(
      switchMap(() =>
        signIn({
          username: email,
          options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP'
          }
        })
      ),
      map((result) => {
        return CognitoAuthMapper.mapToMagicLinkResult(result);
      }),
      catchError((error) => {
        throw CognitoAuthMapper.mapToCognitoAuthError(error);
      })
    );
  }

  /**
   * INFRA.7b. Tells the subgraph which of our addresses is asking, so the
   * emailed link opens here rather than on the environment's fixed base address.
   * Cognito does not pass signIn's clientMetadata to the Lambda that builds the
   * email, which is why this goes through the subgraph instead.
   */
  private noteMagicLinkRequest(email: string): Observable<unknown> {
    // Best-effort, always: a failed or slow note only means the link opens on
    // the base address, while letting it fail here would stop the sign-in.
    if (!this.noteMagicLinkRequestGQL) {
      return of(null);
    }
    return this.noteMagicLinkRequestGQL.mutate({ email }).pipe(
      take(1),
      timeout(NOTE_MAGIC_LINK_REQUEST_TIMEOUT_MS),
      catchError(() => of(null))
    );
  }

  public confirmSignInWithMagicLink(
    token: string
  ): Observable<ConfirmSignInOutput> {
    return from(
      confirmSignIn({
        challengeResponse: token,
        options: { clientMetadata: { alreadyHaveMagicLink: 'true' } }
      })
    ).pipe(
      catchError((error) => {
        throw CognitoAuthMapper.mapToCognitoAuthError(error);
      })
    );
  }

  public cognitoCallback(idToken: string): Observable<CognitoCallback> {
    return this.cognitoCallbackGQL.fetch({ cognitoIdToken: idToken }).pipe(
      map((result) => {
        const token: string = result?.data?.cognitoCallback?.token;
        const refreshToken: string =
          result?.data?.cognitoCallback?.refreshToken;

        if (!token) {
          throw new Error('Invalid token or profile');
        }

        return { token, refreshToken };
      })
    );
  }
}
